import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Search, Heart, TrendingUp, X } from 'lucide-react';

// Tool configuration - centralized mapping
const TOOL_CONFIG = {
  "WW Audio Previewer": {
    id: "wwise",
    component: lazy(() => import('./tools/WwiseTool')),
    fullscreen: true,
    category: "Audio",
    description: "Preview and analyze Wwise audio files"
  },
  "WW Audio ToolKit": {
    id: "wwisextract", 
    component: lazy(() => import('./tools/WwiseToolKit')),
    fullscreen: true,
    category: "Audio",
    description: "Extract and convert Wwise audio files"
  },
  "UE AES Key Scanner": {
    id: "ueaesscanner",
    component: lazy(() => import('./tools/AESKeyScanner')),
    fullscreen: true,
    category: "Security",
    description: "Scan for AES encryption keys in Unreal Engine files"
  },
  "LDS Texture Replacer": {
    id: "LDSTR",
    component: lazy(() => import('./tools/ldsTextureReplacer')),
    fullscreen: true,
    category: "Graphics",
    description: "Replace textures in LDS format files"
  },
  "TMD2 Mesh Viewer": {
    id: "TMD2",
    component: lazy(() => import('./tools/TMD2MeshViewer')),
    fullscreen: true,
    category: "3D",
    description: "View and analyze TMD2 3D mesh files"
  },
  "CAT DDS Editor": {
    id: "cateditor",
    component: lazy(() => import('./tools/catddsReplacer')),
    fullscreen: true,
    category: "Graphics",
    description: "Edit and replace DDS textures in CAT files"
  },
  "BLEACH Particle Editor": {
    id: "ParticlePreviewer",
    component: lazy(() => import('./tools/particleEditor')),
    fullscreen: true,
    category: "Effects",
    description: "Edit particle effects for BLEACH game"
  },
  "Music Visualizer": {
    id: "musicviz",
    component: lazy(() => import('./AudioVisualizerPage')),
    fullscreen: true,
    category: "Audio",
    description: "Create stunning visualizations for audio files"
  },
  "Image Converter": {
    id: "imageconv",
    component: lazy(() => import('./tools/ImageConverter')),
    fullscreen: true,
    category: "Graphics",
    description: "Convert images between various formats"
  },
  "Serial Console": {
    id: "serialconsole",
    component: lazy(() => import('./tools/SerialConsole')),
    fullscreen: true,
    category: "Hardware",
    description: "Communicate with devices via serial connection"
  },
  "Unreal PAK Previewer": {
    id: "UPPV",
    component: lazy(() => import('./tools/UnrealPakPreviewer')),
    fullscreen: true,
    category: "Game Files",
    description: "Preview and extract files from Unreal Engine PAK archives"
  },
  "Switch Audio Converter": {
    id: "saconvert",
    component: lazy(() => import('./tools/switchaudioconverter')),
    fullscreen: true,
    category: "Audio",
    description: "Convert audio files for Nintendo Switch"
  }
};

// Create reverse lookup for easy ID to config mapping
const TOOL_ID_MAP = Object.keys(TOOL_CONFIG).reduce((acc, toolName) => {
  const config = TOOL_CONFIG[toolName];
  acc[config.id] = { ...config, name: toolName };
  return acc;
}, {});

// Analytics service
class AnalyticsService {
  static async trackToolUsage(toolId, toolName, userId = 'anonymous') {
    try {
      const response = await fetch('https://api.spookytools.com/api/analytics/tool-usage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          toolId,
          toolName,
          userId,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          referrer: document.referrer
        })
      });
      
      if (!response.ok) {
        console.warn('Failed to track tool usage:', response.statusText);
      }
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }
  }

  static async getToolAnalytics() {
    try {
      const response = await fetch('https://api.spookytools.com/api/analytics/tool-stats');
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.warn('Failed to fetch analytics:', error);
    }
    return null;
  }
}

// Favorites service (browser storage) - Fixed version
class FavoritesService {
  static STORAGE_KEY = 'spookytools_favorites';

  static getFavorites() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Failed to load favorites:', error);
      return [];
    }
  }

  static addToFavorites(toolId) {
    const favorites = this.getFavorites();
    if (!favorites.includes(toolId)) {
      favorites.push(toolId);
      try {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(favorites));
        console.log('Added to favorites:', toolId);
        return true;
      } catch (error) {
        console.warn('Failed to save favorite:', error);
      }
    }
    return false;
  }

  static removeFromFavorites(toolId) {
    const favorites = this.getFavorites();
    const newFavorites = favorites.filter(id => id !== toolId);
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(newFavorites));
      console.log('Removed from favorites:', toolId);
      return true;
    } catch (error) {
      console.warn('Failed to remove favorite:', error);
    }
    return false;
  }

  static isFavorite(toolId) {
    const favorites = this.getFavorites();
    return favorites.includes(toolId);
  }
}

const ToolsPage = ({ theme }) => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedToolGroup, setSelectedToolGroup] = useState(null);
  const [activeTool, setActiveTool] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Load favorites on component mount
    setFavorites(FavoritesService.getFavorites());
    
    // Load analytics
    AnalyticsService.getToolAnalytics().then(data => {
      if (data) setAnalytics(data);
    });
  }, []);

  useEffect(() => {
    const fetchTools = async () => {
      try {
        // First try to fetch from the API
        try {
          const apiResponse = await fetch('https://api.spookytools.com/api/tools');
          if (apiResponse.ok) {
            const data = await apiResponse.json();
            setTools(data.tools);
            setLoading(false);
            console.log('Successfully loaded tools from API');
            return;
          } else {
            console.warn('API response not OK, falling back to CDN');
          }
        } catch (apiError) {
          console.warn('Error fetching from API, falling back to CDN:', apiError);
        }

        // Fallback to CDN
        const cdnResponse = await fetch('https://cdn.spookytools.com/assets/tools.json');
        if (!cdnResponse.ok) {
          throw new Error('Failed to fetch tools from both API and CDN');
        }
        const data = await cdnResponse.json();
        setTools(data.tools);
        console.log('Successfully loaded tools from CDN fallback');
        setLoading(false);
      } catch (error) {
        console.error('Error fetching tools:', error);
        setLoading(false);
      }
    };

    fetchTools();
  }, []);

  // Get all unique categories
  const categories = ['All', ...new Set(Object.values(TOOL_CONFIG).map(tool => tool.category))];

  // Get tool configuration by name
  const getToolConfig = (toolName) => {
    return TOOL_CONFIG[toolName] || null;
  };

  // Handle tool click
  const handleToolClick = async (tool) => {
    if (tool.isReleased === false) return;

    const toolConfig = getToolConfig(tool.name);
    if (toolConfig) {
      // Track tool usage
      await AnalyticsService.trackToolUsage(toolConfig.id, tool.name);
      setActiveTool(toolConfig.id);
    } else if (tool.subTools && tool.subTools.length) {
      setSelectedToolGroup(tool);
    }
  };

  // Toggle favorite - Fixed version
  const toggleFavorite = (toolName, event) => {
    event.stopPropagation();
    console.log('Toggling favorite for:', toolName);
    
    const toolConfig = getToolConfig(toolName);
    if (!toolConfig) {
      console.warn('No tool config found for:', toolName);
      return;
    }

    const isFav = favorites.includes(toolConfig.id);
    console.log('Current favorite status:', isFav);
    
    let success = false;
    if (isFav) {
      success = FavoritesService.removeFromFavorites(toolConfig.id);
    } else {
      success = FavoritesService.addToFavorites(toolConfig.id);
    }
    
    if (success) {
      const newFavorites = FavoritesService.getFavorites();
      setFavorites(newFavorites);
      console.log('Updated favorites:', newFavorites);
    }
  };

  // Get all tools including sub-tools for filtering
  const getAllTools = (toolsList) => {
    const allTools = [];
    
    toolsList.forEach(tool => {
      // Add the main tool if it has a direct config (not just a category)
      const toolConfig = getToolConfig(tool.name);
      if (toolConfig) {
        allTools.push(tool);
      }
      
      // Add all sub-tools
      if (tool.subTools && tool.subTools.length > 0) {
        tool.subTools.forEach(subTool => {
          allTools.push(subTool);
        });
      }
    });
    
    return allTools;
  };

  // Filter tools based on search and category - Fixed for sub-tools
  const allAvailableTools = getAllTools(tools);
  const filteredTools = allAvailableTools.filter(tool => {
    
    const toolConfig = getToolConfig(tool.name);
    
    // Search filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      tool.name.toLowerCase().includes(searchLower) ||
      (toolConfig?.description && toolConfig.description.toLowerCase().includes(searchLower)) ||
      (toolConfig?.category && toolConfig.category.toLowerCase().includes(searchLower));
    
    // Category filter
    const matchesCategory = selectedCategory === 'All' || 
      (toolConfig && toolConfig.category === selectedCategory);
    
    // Favorites filter
    const matchesFavorites = !showFavoritesOnly || 
      (toolConfig && favorites.includes(toolConfig.id));
    
    const shouldShow = matchesSearch && matchesCategory && matchesFavorites;
    
    return shouldShow;
  });

  // Render active tool component
  const renderActiveTool = () => {
    if (!activeTool) return null;

    const toolConfig = TOOL_ID_MAP[activeTool];
    if (!toolConfig) return null;

    const ToolComponent = toolConfig.component;
    
    return (
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-white text-xl">Loading {toolConfig.name}...</div>
        </div>
      }>
        <ToolComponent theme={theme} />
      </Suspense>
    );
  };

  const renderToolCard = (tool, isSubTool = false) => {
    const isNotReleased = tool.isReleased === false;
    const toolConfig = getToolConfig(tool.name);
    const isFavorite = toolConfig && favorites.includes(toolConfig.id);
    const usageCount = analytics?.toolUsage?.[toolConfig?.id] || 0;

    return (
      <div
        key={tool.name}
        className={`relative w-full mb-4 ${isNotReleased ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'} overflow-hidden transition-all duration-300 hover:scale-102 group`}
        onClick={() => handleToolClick(tool)}
      >
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-orange-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
        
        {/* Favorite button - Show for all tools with config */}
        {toolConfig && !isNotReleased && (
          <button
            onClick={(e) => toggleFavorite(tool.name, e)}
            className={`absolute top-2 right-2 z-10 p-2 rounded-full transition-all duration-200 ${
              isFavorite 
                ? 'bg-red-500 text-white hover:bg-red-600' 
                : 'bg-black bg-opacity-50 text-gray-300 hover:bg-red-500 hover:text-white'
            }`}
          >
            <Heart size={16} fill={isFavorite ? 'currentColor' : 'none'} />
          </button>
        )}

        <div className="relative border-b border-gray-800 overflow-hidden">
          {tool.image && (
            <div className="relative bg-black bg-opacity-80">
              <img
                src={tool.image}
                alt={tool.name}
                className={`w-full h-40 object-cover ${isNotReleased ? 'grayscale' : 'opacity-80 group-hover:opacity-100'} transition-opacity`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
            </div>
          )}
          <div className={`p-3 bg-black bg-opacity-80 ${isNotReleased ? '' : 'group-hover:bg-orange-500'} transition-all duration-200`}>
            <div className="flex justify-between items-start mb-2">
              <h3 className={`text-lg font-bold ${isNotReleased ? 'text-gray-400' : 'text-white group-hover:text-black'} transition-colors`}>
                {tool.name}
              </h3>
              {usageCount > 0 && (
                <div className="flex items-center text-xs text-gray-400 group-hover:text-black">
                  <TrendingUp size={12} className="mr-1" />
                  {usageCount}
                </div>
              )}
            </div>
            {toolConfig?.category && (
              <div className="text-xs text-orange-400 group-hover:text-black mb-1">
                {toolConfig.category}
              </div>
            )}
            {toolConfig?.description && (
              <div className="text-xs text-gray-300 group-hover:text-black">
                {toolConfig.description}
              </div>
            )}
            {/* Debug info - remove this later */}
            <div className="text-xs text-blue-400 mt-1">
              ID: {toolConfig?.id || 'No Config'} | Fav: {isFavorite ? 'Yes' : 'No'}
            </div>
          </div>
        </div>

        {!isSubTool && (
          <div className="absolute top-0 left-0 bg-orange-600 text-black p-1 text-xs uppercase font-bold">
            FEATURED
          </div>
        )}

        {isNotReleased && (
          <div className="absolute top-0 right-0 bg-gray-800 text-white p-1 text-xs uppercase font-bold">
            COMING SOON
          </div>
        )}
      </div>
    );
  };

  const renderSearchAndFilters = () => {
    return (
      <div className="mb-6 space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search tools..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-gray-400 focus:border-orange-500 focus:outline-none"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center">
          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none"
          >
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>

          {/* Favorites Toggle */}
          <button
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            className={`flex items-center px-3 py-2 rounded text-sm transition-colors ${
              showFavoritesOnly 
                ? 'bg-red-500 text-white' 
                : 'bg-gray-900 border border-gray-700 text-gray-300 hover:text-white'
            }`}
          >
            <Heart size={14} className="mr-1" fill={showFavoritesOnly ? 'currentColor' : 'none'} />
            Favorites ({favorites.length})
          </button>

          {/* Analytics Toggle */}
          {analytics && (
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={`flex items-center px-3 py-2 rounded text-sm transition-colors ${
                showAnalytics 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-gray-900 border border-gray-700 text-gray-300 hover:text-white'
              }`}
            >
              <TrendingUp size={14} className="mr-1" />
              Analytics
            </button>
          )}

          {/* Results count */}
          <div className="text-sm text-gray-400 ml-auto">
            {filteredTools.length} tool{filteredTools.length !== 1 ? 's' : ''} found
            {/* Debug info */}
            <div className="text-xs mt-1">
              Available: {allAvailableTools.length} | Categories: {tools.length} | Search: "{searchTerm}" | Category: {selectedCategory} | Favs: {showFavoritesOnly}
            </div>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && analytics && (
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
            <h3 className="text-white font-bold mb-3">Tool Usage Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-gray-400">Total Uses</div>
                <div className="text-white font-bold">{analytics.totalUsage || 0}</div>
              </div>
              <div>
                <div className="text-gray-400">Most Popular</div>
                <div className="text-white font-bold">{analytics.mostPopular || 'N/A'}</div>
              </div>
              <div>
                <div className="text-gray-400">This Week</div>
                <div className="text-white font-bold">{analytics.weeklyUsage || 0}</div>
              </div>
              <div>
                <div className="text-gray-400">Unique Users</div>
                <div className="text-white font-bold">{analytics.uniqueUsers || 0}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="text-white text-xl">Loading tools...</div>
        </div>
      );
    }

    if (selectedToolGroup) {
      return (
        <div className="w-full">
          <div
            className="mb-4 cursor-pointer group"
            onClick={() => setSelectedToolGroup(null)}
          >
            <div className="bg-black bg-opacity-70 border-l-2 border-orange-600 p-2 flex items-center group-hover:bg-orange-500 transition-all duration-200">
              <ChevronLeft className="mr-2 text-white group-hover:text-black transition-colors" />
              <div className="text-sm text-white font-bold tracking-wider group-hover:text-black transition-colors">
                BACK TO TOOLS
              </div>
            </div>
          </div>

          <div className="relative w-full max-w-md mx-auto mb-6">
            <div className="absolute -left-2 top-1/2 h-px w-6 bg-orange-600"></div>
            <div className="absolute -left-4 top-1/2 h-3 w-3 bg-white"></div>
            <h2 className="text-2xl text-white text-center font-mono">
              {selectedToolGroup.name}
            </h2>
            <div className="absolute -right-2 top-1/2 h-px w-6 bg-orange-600"></div>
          </div>

          <div className="w-full h-px bg-orange-600 mb-4 opacity-50 max-w-2xl mx-auto"></div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {selectedToolGroup.subTools?.map(tool => renderToolCard(tool, true)) || (
              <div className="text-gray-500 text-center">No sub-tools available</div>
            )}
          </div>

          <div className="w-full h-px bg-orange-600 mt-4 opacity-50 max-w-2xl mx-auto"></div>
        </div>
      );
    }

    return (
      <div>
        {renderSearchAndFilters()}
        
        {filteredTools.length === 0 ? (
          <div className="text-center text-gray-400 py-8">
            <div className="text-4xl mb-4">🔍</div>
            <div className="text-lg">No tools found</div>
            <div className="text-sm mt-2">Try adjusting your search or filters</div>
            <div className="text-xs mt-4 text-blue-400">
              Debug: {allAvailableTools.length} total tools available ({tools.length} main categories)
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {filteredTools.map(tool => renderToolCard(tool, false))}
          </div>
        )}
      </div>
    );
  };

  // If active tool is selected, render it directly
  if (activeTool) {
    return renderActiveTool();
  }

  return (
    <div className="min-h-screen w-full overflow-hidden flex justify-center items-center">
      {/* Backdrop filter for background video blur */}
      <div className="absolute inset-0 backdrop-blur-sm z-0"></div>

      {/* Top Status Bar */}
      <div className="absolute top-0 left-0 right-0 flex justify-between items-center p-2 z-30">
        <div className="flex items-center space-x-1 bg-black bg-opacity-70 border-l-2 border-orange-600 px-2 py-1">
          <span className="text-xs text-white font-bold uppercase">Tools</span>
        </div>

        <div className="text-gray-400 text-xs bg-black bg-opacity-70 px-2 py-1">
          {currentTime.toLocaleString()}
        </div>
      </div>

      {/* Centered content container with max-width */}
      <div className="w-full max-w-6xl h-full pt-12 pb-16 px-4 overflow-y-auto relative z-10 flex flex-col justify-center">
        <div className="mx-auto">
          {/* Logo/Header */}
          <div className="relative w-full max-w-lg mx-auto mb-6">
            <div className="absolute -left-2 top-1/2 h-px w-6 bg-orange-600 hidden md:block"></div>
            <div className="absolute -left-4 top-1/2 h-3 w-3 bg-white hidden md:block"></div>
            <div className="bg-black bg-opacity-60 p-2">
              <img
                src={theme.images.tools}
                className="w-full max-w-md mx-auto block"
                alt="tools"
              />
            </div>
            <div className="absolute -right-2 top-1/2 h-px w-6 bg-orange-600 hidden md:block"></div>
          </div>

          <div className="w-full h-px bg-orange-600 mb-6 opacity-50"></div>

          <div className="bg-black bg-opacity-60 p-4">
            {renderContent()}
          </div>

          <div className="w-full h-px bg-orange-600 mt-6 opacity-50"></div>
        </div>
      </div>

      {/* Back Button */}
      <Link
        to="/"
        className="fixed bottom-4 left-4 z-20 flex items-center bg-black bg-opacity-70 border-l-2 border-orange-600 px-3 py-2 group hover:bg-orange-500 transition-all duration-200"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 mr-2 text-white group-hover:text-black transition-all duration-200"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        <span className="text-white text-sm font-bold tracking-wider uppercase group-hover:text-black transition-all duration-200">
          Back to Main Menu
        </span>
      </Link>

      {/* Status Info */}
      <div className="fixed bottom-2 right-2 text-gray-500 text-[10px] bg-black bg-opacity-70 p-1 rounded z-30">
        Connection Status: <span className="text-green-500">Connected</span>
      </div>
    </div>
  );
};

export default ToolsPage;